import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Camera, Upload, Image, Sparkles, ArrowRight, AlertCircle, 
  Download, X, Facebook, Instagram
} from 'lucide-react';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { hasTrackingConsent } from '../components/CookieConsent';
import useVirtualTryOn, { SIZES, FIT_OPTIONS } from '../hooks/useVirtualTryOn';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FreeTrialPage() {
  const navigate = useNavigate();
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [browserFingerprint, setBrowserFingerprint] = useState(null);
  const [biometricConsent, setBiometricConsent] = useState(false);
  const consentLoggedRef = useRef(false);
  const fingerprintRef = useRef(null);

  // Log biometric consent acceptance to backend audit trail (BIPA / GDPR)
  const logBiometricConsent = async () => {
    if (consentLoggedRef.current) return;
    consentLoggedRef.current = true;
    try {
      // Fetch current policy version, then log
      const verRes = await fetch(`${API_URL}/api/biometric-consent/current-version`);
      const versions = verRes.ok ? await verRes.json() : {
        biometric_policy_version: 'biometric_policy_v2.0_may_29_2026',
        privacy_policy_version: 'privacy_policy_v1.0_may_29_2026',
      };
      await fetch(`${API_URL}/api/biometric-consent/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policy_version: versions.biometric_policy_version,
          privacy_policy_version: versions.privacy_policy_version,
          fingerprint: fingerprintRef.current || null,
          user_agent: navigator.userAgent,
        }),
      });
    } catch (err) {
      // Don't block the user on logging failure; checkbox UX continues
      console.warn('Biometric consent log failed:', err);
      consentLoggedRef.current = false; // allow retry on next check
    }
  };

  const handleConsentChange = (checked) => {
    setBiometricConsent(checked);
    if (checked) {
      logBiometricConsent();
    }
  };

  const tryon = useVirtualTryOn({
    mode: 'free-trial',
    fingerprint: fingerprintRef.current,
    onComplete: (result) => {
      setTrialStatus(prev => ({
        ...prev,
        images_used: (prev?.images_used || 0) + 1,
        images_remaining: result.images_remaining,
        can_generate: result.images_remaining > 0
      }));
      // Meta Pixel — ViewContent. Guarded by fbq presence; fbq only exists
      // after the visitor has accepted analytics cookies via vf_cookie_consent.
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'ViewContent', { content_name: 'try_on_generated' });
      }
    },
    onTrialExceeded: () => {
      setTrialStatus(prev => ({ ...prev, can_generate: false }));
      navigate('/subscribe');
    },
  });

  const {
    userPhotoPreview, clothesPreview, generatedImage, generating,
    progress: generatingProgress, error, size, fitPreference,
    setSize, setFitPreference, handleImageUpload, handleDownload,
    handleShare, clearUserPhoto, clearClothesImage, reset: resetForm,
  } = tryon;

  // Generate a random per-browser trial session ID (NOT a device fingerprint).
  // This is a "strictly necessary" cookie under GDPR — required for the free
  // trial service the user is actively requesting. It doesn't collect any
  // device characteristics, so it does not require explicit tracking consent.
  const ensureTrialSessionId = () => {
    let sid = localStorage.getItem('vf_trial_sid');
    if (!sid) {
      const rand = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      sid = 'sid_' + rand;
      localStorage.setItem('vf_trial_sid', sid);
    }
    return sid;
  };

  // Initialize browser fingerprint on mount — only use device fingerprint if user
  // accepted tracking cookies. Otherwise use the anonymous trial session ID so
  // every browser still gets its own quota (fixes shared-IP "already used" bug).
  useEffect(() => {
    const initFingerprint = async () => {
      if (!hasTrackingConsent()) {
        // User rejected or hasn't decided — use anonymous per-browser trial session ID
        // instead of IP-only (which incorrectly shares quotas across users on NAT/CGNAT).
        const sid = ensureTrialSessionId();
        console.log('Tracking cookies not accepted, using anonymous trial session ID');
        setBrowserFingerprint(sid);
        fingerprintRef.current = sid;
        return;
      }

      try {
        const cachedFp = localStorage.getItem('vf_browser_fp');
        if (cachedFp) {
          setBrowserFingerprint(cachedFp);
          fingerprintRef.current = cachedFp;
          console.log('Using cached fingerprint');
          return;
        }
        
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        const visitorId = result.visitorId;
        
        localStorage.setItem('vf_browser_fp', visitorId);
        setBrowserFingerprint(visitorId);
        fingerprintRef.current = visitorId;
        console.log('Generated new fingerprint');
      } catch (err) {
        console.error('Failed to generate fingerprint:', err);
        const fallbackFp = generateFallbackFingerprint();
        localStorage.setItem('vf_browser_fp', fallbackFp);
        setBrowserFingerprint(fallbackFp);
        fingerprintRef.current = fallbackFp;
      }
    };
    
    initFingerprint();

    // Re-init if consent changes while on page
    const onConsentChange = () => initFingerprint();
    window.addEventListener('cookieConsentChanged', onConsentChange);
    return () => window.removeEventListener('cookieConsentChanged', onConsentChange);
  }, []);

  // Fallback fingerprint generation
  const generateFallbackFingerprint = () => {
    const data = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      navigator.hardwareConcurrency || 'unknown',
      navigator.platform
    ].join('|');
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return 'fb_' + Math.abs(hash).toString(36) + '_' + Date.now().toString(36);
  };

  useEffect(() => {
    // Wait for fingerprint before fetching status
    if (browserFingerprint) {
      fetchTrialStatus();
    }
  }, [browserFingerprint]);

  const fetchTrialStatus = async () => {
    try {
      const fp = fingerprintRef.current;
      const url = fp 
        ? `${API_URL}/api/free-trial/status?fingerprint=${encodeURIComponent(fp)}`
        : `${API_URL}/api/free-trial/status`;
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setTrialStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch trial status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = () => {
    if (trialStatus && !trialStatus.can_generate) {
      tryon.setError('Free trial limit reached. Please subscribe to continue!');
      return;
    }
    const fp = fingerprintRef.current;
    if (!fp) {
      tryon.setError('Initializing... Please try again in a moment.');
      return;
    }
    tryon.handleGenerate();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-rose-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // If trial expired, show subscribe message
  if (trialStatus && !trialStatus.can_generate && trialStatus.images_used >= trialStatus.limit) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-10 h-10 text-rose-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Free Trial Complete!</h1>
          <p className="text-gray-600 mb-6">
            You've used your {trialStatus.limit} free try-on images. 
            Subscribe to continue creating unlimited virtual try-ons!
          </p>
          <div className="space-y-4">
            <Link
              to="/subscribe"
              className="block w-full py-3 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              data-testid="subscribe-cta"
            >
              Subscribe Now - Starting at $9/mo
            </Link>
            <Link to="/" className="block text-gray-500 hover:text-gray-700">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <Sparkles className="w-6 h-6 text-rose-500" />
            <span className="text-xl font-bold bg-gradient-to-r from-rose-400 to-orange-400 bg-clip-text text-transparent">
              VirtualFit
            </span>
          </Link>
          <div className="flex items-center space-x-4">
            <div className="bg-rose-100 text-rose-700 px-4 py-2 rounded-full text-sm font-medium">
              Free Trial: {trialStatus?.images_remaining || 0} of {trialStatus?.limit || 2} remaining
            </div>
            <Link to="/login" className="text-gray-600 hover:text-gray-800 font-medium">Login</Link>
            <Link to="/subscribe" className="px-4 py-2 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-lg font-medium hover:shadow-lg transition-all">
              Subscribe
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Try It Free!</h1>
          <p className="text-gray-600">
            Upload your photo and a clothing image to see the magic. You have {trialStatus?.images_remaining || 2} free try-on(s) remaining.
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center max-w-2xl mx-auto">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Generated Result View */}
        {generatedImage ? (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="font-semibold text-gray-800 mb-4 text-center text-xl">Your Virtual Try-On</h3>
              <div className="rounded-xl overflow-hidden mb-6">
                <img 
                  src={generatedImage.startsWith('http') ? generatedImage : (generatedImage.startsWith('data:') ? generatedImage : `data:image/png;base64,${generatedImage}`)}
                  alt="Generated look" 
                  className="w-full max-h-[500px] object-contain mx-auto"
                />
              </div>
              
              {/* AI Disclaimer */}
              <p className="text-xs text-gray-400 text-center mb-4">This is an AI-generated image. Actual fit, color, and texture may differ from the real product.</p>
              
              {/* Download & Share Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <button
                  onClick={handleDownload}
                  className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                  data-testid="download-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Image
                </button>
                <button
                  onClick={resetForm}
                  className="flex items-center justify-center px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
                >
                  Try Another Look
                </button>
              </div>
              
              {/* Social Share */}
              <div className="border-t pt-6">
                <p className="text-center text-gray-600 mb-4">Share your look</p>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => handleShare('twitter')}
                    className="p-3 bg-black text-white rounded-full hover:opacity-90 transition-opacity"
                    title="Share on X"
                    data-testid="share-x"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => handleShare('facebook')}
                    className="p-3 bg-[#4267B2] text-white rounded-full hover:opacity-90 transition-opacity"
                    title="Share on Facebook"
                    data-testid="share-facebook"
                  >
                    <Facebook className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleShare('instagram')}
                    className="p-3 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white rounded-full hover:opacity-90 transition-opacity"
                    title="Share on Instagram"
                    data-testid="share-instagram"
                  >
                    <Instagram className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Subscribe CTA after generation */}
            <div className="mt-8 bg-gradient-to-r from-rose-500 to-orange-500 rounded-2xl p-6 text-center text-white">
              <h2 className="text-xl font-bold mb-2">Love What You See?</h2>
              <p className="mb-4 text-white/90">Subscribe to unlock unlimited try-ons and save your looks!</p>
              <Link
                to="/subscribe"
                className="inline-block px-6 py-3 bg-white text-rose-500 rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                View Plans <ArrowRight className="w-5 h-5 inline ml-2" />
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Upload Section */}
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
              {/* Biometric Consent Checkbox */}
              <div className="md:col-span-3 bg-white rounded-2xl shadow-lg p-5">
                <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="biometric-consent-label">
                  <input
                    type="checkbox"
                    checked={biometricConsent}
                    onChange={(e) => handleConsentChange(e.target.checked)}
                    className="mt-1 w-5 h-5 min-w-[20px] accent-rose-500 rounded cursor-pointer"
                    data-testid="biometric-consent-checkbox"
                  />
                  <span className="text-sm text-gray-600 leading-relaxed">
                    I am at least 18 years old and I explicitly consent for VirtualFit to process my biometric data (facial geometry and body measurements) for the purpose of generating my virtual try-on image, as described in the{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-rose-500 hover:underline font-medium">Privacy Policy</a>
                    {' '}and{' '}
                    <a href="/biometric-policy" target="_blank" rel="noopener noreferrer" className="text-rose-500 hover:underline font-medium">Biometric Data Policy</a>.
                    <br /><br />
                    I understand that my biometric data will be processed in real-time and will not be stored beyond this session unless I create an account.
                  </span>
                </label>
              </div>
              {/* Your Photo */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                  <Camera className="w-5 h-5 mr-2 text-rose-500" />
                  Your Photo
                </h3>
                <div className="aspect-[3/4] bg-gray-100 rounded-xl overflow-hidden relative">
                  {userPhotoPreview ? (
                    <>
                      <img src={userPhotoPreview} alt="Your photo" className="w-full h-full object-cover" />
                      <button
                        onClick={() => clearUserPhoto()}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <label className={`w-full h-full flex flex-col items-center justify-center transition-colors ${biometricConsent ? 'cursor-pointer hover:bg-gray-200' : 'cursor-not-allowed opacity-50'}`}>
                      <Upload className="w-10 h-10 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">{biometricConsent ? 'Upload Photo' : 'Upload Photo'}</span>
                      <span className="text-xs text-gray-400 mt-1 text-center px-2">
                        {biometricConsent ? 'Full-body, front-facing photo on a plain background' : 'Check consent box above to enable'}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'user')}
                        className="hidden"
                        disabled={!biometricConsent}
                        data-testid="upload-user-photo"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Clothing */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                  <Image className="w-5 h-5 mr-2 text-rose-500" />
                  Clothing
                </h3>
                <div className="aspect-[3/4] bg-gray-100 rounded-xl overflow-hidden relative">
                  {clothesPreview ? (
                    <>
                      <img src={clothesPreview} alt="Clothing" className="w-full h-full object-cover" />
                      <button
                        onClick={() => clearClothesImage()}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <label className={`w-full h-full flex flex-col items-center justify-center transition-colors ${biometricConsent ? 'cursor-pointer hover:bg-gray-200' : 'cursor-not-allowed opacity-50'}`}>
                      <Upload className="w-10 h-10 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Upload Clothing</span>
                      <span className="text-xs text-gray-400 mt-1 text-center px-2">
                        {biometricConsent ? 'Single item on a white or plain background' : 'Check consent box above to enable'}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'clothes')}
                        className="hidden"
                        disabled={!biometricConsent}
                        data-testid="upload-clothing"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Result Preview */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                  <Sparkles className="w-5 h-5 mr-2 text-rose-500" />
                  Your Look
                </h3>
                <div className="aspect-[3/4] bg-gray-100 rounded-xl overflow-hidden relative">
                  {generating ? (
                    <div className="w-full h-full flex flex-col items-center justify-center">
                      <div className="w-12 h-12 border-4 border-rose-400 border-t-transparent rounded-full animate-spin mb-4"></div>
                      <span className="text-sm text-gray-500">Creating your look...</span>
                      <span className="text-xs text-gray-400 mt-1">This may take 10-20 seconds</span>
                    </div>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                      <Image className="w-10 h-10 mb-2" />
                      <span className="text-sm">Result will appear here</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Size & Fit Preferences */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="font-semibold text-gray-800 mb-4">Size & Fit Preferences</h3>
                
                {/* Size Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
                  <div className="flex flex-wrap gap-2">
                    {SIZES.map((s) => (
                      <button
                        key={s}
                        onClick={() => setSize(s)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          size === s
                            ? 'bg-rose-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        data-testid={`size-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Fit Preference */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fit Preference</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {FIT_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setFitPreference(option.value)}
                        className={`p-3 rounded-lg text-center transition-all ${
                          fitPreference === option.value
                            ? 'bg-rose-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        data-testid={`fit-${option.value}`}
                      >
                        <div className="font-medium">{option.label}</div>
                        <div className={`text-xs mt-1 ${fitPreference === option.value ? 'text-rose-100' : 'text-gray-500'}`}>
                          {option.description}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <div className="text-center">
              <button
                onClick={handleGenerate}
                disabled={!biometricConsent || generating || !tryon.userPhoto || !tryon.clothesImage}
                className="px-8 py-4 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-xl font-semibold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center mx-auto"
                data-testid="generate-tryon-btn"
              >
                {generating ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    {generatingProgress || 'Generating...'}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Try-On ({trialStatus?.images_remaining || 2} free remaining)
                  </>
                )}
              </button>
            </div>

            {/* Subscribe CTA */}
            <div className="mt-12 bg-gradient-to-r from-rose-500 to-orange-500 rounded-2xl p-8 text-center text-white max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold mb-3">Love What You See?</h2>
              <p className="mb-6 text-white/90">
                Subscribe to unlock unlimited try-ons, save your looks, and access premium features!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/subscribe"
                  className="px-6 py-3 bg-white text-rose-500 rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center"
                >
                  View Plans <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <Link
                  to="/register"
                  className="px-6 py-3 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all"
                >
                  Create Account
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
